/* eslint-disable no-duplicate-imports */
/* eslint-disable react/jsx-props-no-spreading */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import useCurrentUserQuery from './Utils/useCurrentUserQuery';
import useCardIDsQuery from './Utils/useCardIDsQuery';
import Input from '5-react-form-apps/FormElements/Input';
import SelectCustom from '5-react-form-apps/FormElements/SelectCustom';
import CardSettingsOptions from './CardSettingsOptions';
import InlineMessage from '3-components/InlineMessage/InlineMessage';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import type { CardIDDataType } from './Utils/useCardIDsQuery';
import { CARD_STATUS_ACTIVE, CARD_STATUS_INACTIVE, CARD_STATUS_ORDERED } from './constants';
import Image from '2-core-elements/Image/Image';

interface CardSettingsProps {
    username: string;
    changePinUrl: string;
    reportCardUrl: string;
    cardImgSrc: string;
}

const CardSettings = (props: CardSettingsProps): JSX.Element => {
    const { username, changePinUrl, reportCardUrl, cardImgSrc } = props;
    const { data: currentUser, isLoading: isUserLoading } = useCurrentUserQuery();

    const cardIds = currentUser?.cards?.map(card => {
        return card.id;
    }) || [];

    const cardQueries = useCardIDsQuery(cardIds);

    const allCardDetails = cardQueries.map(q => {
        return q.data;
    }).filter((c): c is CardIDDataType => {
        return !!c;
    });

    const isAnyLoading = isUserLoading || cardQueries.some(q => {
        return q.isLoading;
    });

    // Sort so that a card with status 1 or 5 is first in the list
    const sortedCardDetails = useMemo(() => {
        return [
            ...allCardDetails.filter(card => {
                return card.status === CARD_STATUS_ACTIVE || card.status === CARD_STATUS_INACTIVE;
            }),
            ...allCardDetails.filter(card => {
                return card.status !== CARD_STATUS_ACTIVE && card.status !== CARD_STATUS_INACTIVE;
            })
        ];
    }, [allCardDetails]);

    // Wait until sortedCardDetails is populated and isAnyLoading is false before setting activeCard
    const [activeCard, setActiveCard] = useState<CardIDDataType | null>(null);
    const [inlineMessage, setInlineMessage] = useState<{ status: 'active' | 'inactive' } | null>(null);

    useEffect(() => {
        if (!isAnyLoading && sortedCardDetails.length > 0 && !activeCard) {
            setActiveCard(sortedCardDetails[0]);
        }
    }, [isAnyLoading, sortedCardDetails, activeCard]);

    // Refetch card data after status change
    const handleStatusChange = (): void => {
        if (!activeCard) {
            return;
        }
        cardQueries.forEach(q => {
            if (q.data?.id === activeCard.id) {
                q.refetch();
            }
        });
    };

    // If any card has status 0, disable all cards
    const anyCardStatusZero = allCardDetails.some(card => {
        return card.status === CARD_STATUS_ORDERED;
    });

    // Use allCardDetails for all card logic
    if (isAnyLoading || !activeCard) {
        return (
            <div>
                <LoadingIndicator className="sg-eg-loader" />
            </div>
        );
    }

    const selectOptions = sortedCardDetails.map(card => {
        return { label: card.pan.replace(/X/g, '*'), value: card.id };
    });
    const selectValue = selectOptions.find(opt => {
        return opt.value === activeCard.id;
    }) || null;

    // Helper to format expiry as MM/YY from ISO date string
    const formatExpiry = (expiry: string | undefined): string => {
        if (!expiry) {
            return '';
        }
        const date = new Date(expiry);
        if (!isNaN(date.getTime())) {
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const yy = String(date.getFullYear()).slice(-2);
            return `${mm}/${yy}`;
        }
        return '';
    };

    return (
        <>
            <div className="card-settings-alert">
                {anyCardStatusZero ? (
                    <InlineMessage status="info">
                        <p>A new card has been ordered/issued. You can activate it upon receiving it to continue using your account.</p>
                    </InlineMessage>
                ) : (activeCard.status !== CARD_STATUS_ACTIVE && activeCard.status !== CARD_STATUS_INACTIVE ? (
                    <InlineMessage status="error">
                        <p>This MCC Plus card is currently unavailable. Call MCC to find out more about the status of your card.</p>
                    </InlineMessage>
                ) : null)}
            </div>
            <div className="card-settings-content">
                <div className={`card-settings-card-details${(activeCard.status !== CARD_STATUS_ACTIVE && activeCard.status !== CARD_STATUS_INACTIVE) || anyCardStatusZero ? ' disabled' : ''}`}>
                    {allCardDetails.length > 1 && (
                        <div className="card-selection">
                            <label htmlFor="cardSelect" className="input-label">Select card</label>
                            <SelectCustom
                                id="cardSelect"
                                value={selectValue}
                                options={selectOptions}
                                onChange={(option: { value: string | number } | null): void => {
                                    const selected = allCardDetails.find(card => {
                                        return card.id === option?.value;
                                    });
                                    if (selected) {
                                        setActiveCard(selected);
                                    }
                                }}
                                canUnsetValue={false}
                            />
                        </div>
                    )}
                    <Image
                        imgSrc={cardImgSrc}
                        imgAlt="MCC Plus card"
                    />
                </div>
                <div className="card-settings-user-details">
                    <div className="name">
                        <label htmlFor="cardHolderName" className="input-label">Name</label>
                        <Input
                            id="cardHolderName"
                            type="text"
                            placeholder="Name"
                            fullWidth={true}
                            value={username}
                            disabled={true}
                            autoComplete="cc-name"
                        />
                    </div>
                    <div className="card-details">
                        <div className="card-number">
                            <label htmlFor="cardNumber" className="input-label">Card number</label>
                            <Input
                                id="cardNumber"
                                type="text"
                                placeholder="Card Number"
                                fullWidth={true}
                                value={activeCard.pan.replace(/X/g, '*')}
                                disabled={true}
                                autoComplete="cc-number"
                            />
                        </div>
                        <div className="expiry-date">
                            <label htmlFor="expiryDate" className="input-label">Expiry</label>
                            <Input
                                id="expiryDate"
                                type="text"
                                placeholder="Expiry Date"
                                fullWidth={true}
                                value={formatExpiry(activeCard.expiry)}
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={`card-settings-options${(activeCard.status !== CARD_STATUS_ACTIVE && activeCard.status !== CARD_STATUS_INACTIVE) || anyCardStatusZero ? ' disabled' : ''}`}>
                {inlineMessage && !anyCardStatusZero && (
                    <InlineMessage status="success">
                        <p>MCC Plus Card now {inlineMessage.status === 'active' ? 'Active' : 'Inactive'}</p>
                    </InlineMessage>
                )}
                <CardSettingsOptions
                    card={activeCard}
                    setInlineMessage={setInlineMessage}
                    onStatusChange={handleStatusChange}
                    changePinUrl={changePinUrl}
                    reportCardUrl={reportCardUrl}
                    anyCardStatusZero={anyCardStatusZero}
                />
            </div>
        </>
    );
};

const queryClient = new QueryClient();

interface CardSettingsInitProps {
    el: HTMLDivElement;
    props: any;
}

const CardSettingsInit = ({ el, props }: CardSettingsInitProps): void => {
    const root = ReactDOMClient.createRoot(el);
    root.render(
        <QueryClientProvider client={queryClient}>
            <CardSettings {...props} />
        </QueryClientProvider>
    );
};

export default CardSettingsInit;
